import { formatNumToStandardNotation } from '@modules/common/utils';
import {
  DEFAULT_COMPARE_DURATION_A_NAME,
  DEFAULT_COMPARE_DURATION_B_NAME,
  DEFAULT_COMPARE_FILTER_A_NAME,
  DEFAULT_COMPARE_FILTER_B_NAME,
} from '@modules/quantify/constants';
import { useGetQuantifyFormulaDisplayName } from '@modules/quantify/hooks';
import { useGetCompareColors } from '@modules/quantify/hooks/useGetCompareColors';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { CompareReportDataPoint } from '@modules/quantify/store/types';
import { CircleRounded } from '@mui/icons-material';
import { Link, Stack, Typography } from '@mui/material';
import { useCallback } from 'react';
import { EQLFunctionName } from 'v2/core/repository/eql/types';

interface CompareChartTooltipRecordsCountProps {
  payload: CompareReportDataPoint | null;
  onViewFeedback: ({ isFieldA }: { isFieldA: boolean }) => void;
}

export const CompareChartTooltipRecordsCount = ({
  payload,
  onViewFeedback,
}: CompareChartTooltipRecordsCountProps) => {
  const { colorA, colorB } = useGetCompareColors();
  const compareTo = useQuantifyStore(({ query }) => query.compareQuery);
  const formulaID = useQuantifyStore(
    ({ query }) => query.formulaQuery?.eqlFunction,
  );
  const measuredAsCriteria = useQuantifyStore(
    ({ query }) => query.formulaQuery?.criteria,
  );
  const { formulaDisplayName } = useGetQuantifyFormulaDisplayName();
  const compareFilterAName = useQuantifyStore(
    ({ query }) =>
      query.compareMetadata?.baseQueryName || DEFAULT_COMPARE_FILTER_A_NAME,
  );
  const compareFilterBName = useQuantifyStore(
    ({ query }) =>
      query.compareMetadata?.deltaQueryName || DEFAULT_COMPARE_FILTER_B_NAME,
  );
  const compareDurationAName = useQuantifyStore(
    ({ query }) =>
      query.compareMetadata?.baseQueryName || DEFAULT_COMPARE_DURATION_A_NAME,
  );
  const compareDurationBName = useQuantifyStore(
    ({ query }) =>
      query.compareMetadata?.deltaQueryName || DEFAULT_COMPARE_DURATION_B_NAME,
  );

  const forumulaSuffix =
    formulaID === EQLFunctionName.NormaliseByPercent ? '%' : '';

  const renderFieldRecordCount = useCallback(
    ({ isFieldA }: { isFieldA: boolean }) => {
      const color = isFieldA ? colorA : colorB;
      const value = isFieldA ? payload?.currValue : payload?.prevValue;
      const compareFilterLabel = isFieldA
        ? compareFilterAName
        : compareFilterBName;
      const compareDurationLabel = isFieldA
        ? compareDurationAName
        : compareDurationBName;
      const compareFieldLabel = !compareTo?.whereClause
        ? compareDurationLabel
        : compareFilterLabel;

      return (
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <CircleRounded sx={{ width: 14 }} style={{ color }} />
          <Stack spacing={1} direction="row" alignItems="baseline">
            <Typography variant="body2">{compareFieldLabel}:</Typography>
            {value ? (
              <Link
                component="button"
                variant="body2"
                onClick={() => onViewFeedback({ isFieldA })}
                color="primary"
                underline="hover"
              >
                {formatNumToStandardNotation(value)}
                {forumulaSuffix}
              </Link>
            ) : (
              <Typography
                color="text.secondary"
                component="span"
                variant="body2"
              >
                0{forumulaSuffix}
              </Typography>
            )}
          </Stack>
        </Stack>
      );
    },
    [
      colorA,
      colorB,
      payload?.currValue,
      payload?.prevValue,
      compareFilterAName,
      compareFilterBName,
      compareDurationAName,
      compareDurationBName,
      compareTo?.whereClause,
      forumulaSuffix,
      onViewFeedback,
    ],
  );

  const renderCriteriaLabel = () => {
    const criteriaDisplayName = measuredAsCriteria
      ? measuredAsCriteria.displayName
      : 'feedback records';

    if (formulaID === EQLFunctionName.NormaliseByPercent) {
      return `% of total ${criteriaDisplayName} matching`;
    }

    if (formulaID === EQLFunctionName.Count) {
      return `# ${criteriaDisplayName}`;
    }

    return `${formulaDisplayName} of ${criteriaDisplayName}`;
  };

  return (
    <Stack spacing={0.5} px={1.5}>
      <Typography variant="body2" color="text.secondary">
        {renderCriteriaLabel()}:
      </Typography>
      {renderFieldRecordCount({ isFieldA: true })}
      {renderFieldRecordCount({ isFieldA: false })}
    </Stack>
  );
};
